import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, ExternalLink } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const empty = { slug: "", title: "", excerpt: "", cover_image: "", author: "CashPark Editorial", read_minutes: 5, content_html: "", tags: "" };

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null = create
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/blogs");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditingSlug(null);
    setForm(empty);
    setOpen(true);
  };
  const startEdit = (p) => {
    setEditingSlug(p.slug);
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    setOpen(true);
  };

  const save = async () => {
    if (!form.slug || !form.title || !form.excerpt || !form.cover_image || !form.content_html) {
      toast.error("Slug, title, excerpt, cover image and content are required");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
      read_minutes: Number(form.read_minutes) || 5,
    };
    delete payload._id;
    try {
      if (editingSlug) {
        await adminApi.put(`/admin/blogs/${editingSlug}`, payload);
        toast.success("Post updated");
      } else {
        await adminApi.post("/admin/blogs", payload);
        toast.success("Post created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    try {
      await adminApi.delete(`/admin/blogs/${slug}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="blog-manager-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold text-[#0B192C]">Blog posts ({posts.length})</h3>
        <Button onClick={startCreate} className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] h-10 px-5 font-semibold" data-testid="blog-create-button">
          <Plus size={16} className="mr-1.5" /> New post
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">No posts yet.</div>
        ) : (
          <ul className="divide-y divide-slate-200" data-testid="blog-list">
            {posts.map((p) => (
              <li key={p.slug} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5" data-testid={`blog-row-${p.slug}`}>
                <img src={p.cover_image} alt="" className="w-20 h-14 object-cover rounded-lg bg-slate-100" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0B192C] truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 truncate">/{p.slug} · {p.read_minutes} min · {new Date(p.published_at).toLocaleDateString()}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a href={`/blogs/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-[#0B192C]" title="View" data-testid={`blog-view-${p.slug}`}>
                    <ExternalLink size={16} />
                  </a>
                  <button onClick={() => startEdit(p)} className="p-2 text-slate-500 hover:text-[#FF6B00]" title="Edit" data-testid={`blog-edit-${p.slug}`}>
                    <Pencil size={16} />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 text-slate-500 hover:text-red-600" title="Delete" data-testid={`blog-delete-${p.slug}`}>
                        <Trash2 size={16} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                        <AlertDialogDescription>"{p.title}" will be permanently removed.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p.slug)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle>{editingSlug ? "Edit blog post" : "New blog post"}</DialogTitle>
          </DialogHeader>

          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div>
              <Label>Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="my-post-slug" data-testid="blog-slug-input" className="mt-1.5 h-10 rounded-lg" />
            </div>
            <div>
              <Label>Read minutes</Label>
              <Input type="number" min="1" value={form.read_minutes} onChange={(e) => setForm({ ...form, read_minutes: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
            </div>
            <div className="sm:col-span-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title-input" className="mt-1.5 h-10 rounded-lg" />
            </div>
            <div className="sm:col-span-2">
              <Label>Excerpt</Label>
              <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1.5 rounded-lg" />
            </div>
            <div className="sm:col-span-2">
              <Label>Cover image URL</Label>
              <Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="mt-1.5 h-10 rounded-lg" placeholder="https://…" />
            </div>
            <div>
              <Label>Author</Label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="mt-1.5 h-10 rounded-lg" />
            </div>
            <div>
              <Label>Tags (comma separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1.5 h-10 rounded-lg" placeholder="fx, remittance" />
            </div>
            <div className="sm:col-span-2">
              <Label>Content (HTML)</Label>
              <Textarea rows={10} value={form.content_html} onChange={(e) => setForm({ ...form, content_html: e.target.value })} data-testid="blog-content-input" className="mt-1.5 rounded-lg font-mono text-sm" placeholder="<p>Body…</p><h2>Heading</h2>…" />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="blog-save-button" className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
